import dayjs from "dayjs";
import { LANGUAGE } from "services/general";
import { Calendar2 } from "components/shared/icons";

/**
 * Formats a date value with dayjs using a fixed pattern (not locale-aware month names).
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - Value parseable by dayjs
 * @param {string} [format="YYYY-MM-DD"] - dayjs format token string (e.g. `"YYYY-MM-DD HH:mm"`)
 * @returns {string} Formatted string, or `""` if missing or invalid
 */
export const formatDate = (dateString, format = "YYYY-MM-DD") => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  return date.isValid() ? date.format(format) : "";
};

const ARABIC_MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const ENGLISH_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Formats a calendar date for display using month names in the **current app language**
 * (`LANGUAGE` from `services/general.js`).
 * Pattern: `DD` (zero-padded) + space + localized month name + space + `YYYY`.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - ISO date or other value parseable by dayjs
 * @returns {string} e.g. Arabic `"01 فبراير 2026"`, English `"01 February 2026"`; `""` if missing or invalid
 */
export const formatLocalizedDate = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const day = String(date.date()).padStart(2, "0");
  const months = LANGUAGE === "ar" ? ARABIC_MONTHS : ENGLISH_MONTHS;
  const month = months[date.month()];
  const year = date.year();

  return `${day} ${month} ${year}`;
};

/**
 * A day and its month, in the current app language — `"1 سبتمبر"` / `"1 September"`.
 *
 * Composed from the month arrays for the same reason as its siblings, and not from
 * dayjs's own `format()`: the bundled `ar` locale ships a `postformat` that rewrites
 * every digit to Arabic-Indic, and whether dayjs applies it depends on which plugins
 * are loaded. A date range printed beside Western-digit dates must not be one plugin
 * away from changing numeral system.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - Value parseable by dayjs
 * @returns {string} e.g. `"1 سبتمبر"`; `""` if missing or invalid
 */
export const formatDayMonth = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const months = LANGUAGE === "ar" ? ARABIC_MONTHS : ENGLISH_MONTHS;

  return `${date.date()} ${months[date.month()]}`;
};

/**
 * A month's name alone, in the current app language — `"سبتمبر"` / `"September"`. Composed from
 * the month arrays for the same reason as `formatDayMonth`.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - Value parseable by dayjs
 * @returns {string} `""` if missing or invalid
 */
export const formatMonthName = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  return (LANGUAGE === "ar" ? ARABIC_MONTHS : ENGLISH_MONTHS)[date.month()];
};

/**
 * A month and its year, in the current app language — `"سبتمبر 2026"` / `"September 2026"`.
 *
 * Built from the same month arrays as `formatLocalizedDate` rather than
 * `toLocaleDateString`, which renders the year in Arabic-Indic digits (`٢٠٢٦`) on the
 * Arabic locale and would print a different numeral system from every other date on the
 * page.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - Value parseable by dayjs
 * @returns {string} e.g. `"سبتمبر 2026"`; `""` if missing or invalid
 */
export const formatMonthYear = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const months = LANGUAGE === "ar" ? ARABIC_MONTHS : ENGLISH_MONTHS;

  return `${months[date.month()]} ${date.year()}`;
};

/**
 * Same date half as `formatLocalizedDate`, then appends a 12-hour clock time after a ` | ` separator.
 * Uses the dayjs `hh:mm A` token so the hour is the 12-hour one the AM/PM label describes
 * (`date.hour()` is 0–23 and produced impossible times like `16:16 PM`), and both parts are padded.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - ISO datetime or value parseable by dayjs
 * @returns {string} e.g. `"01 February 2026 | 03:45 PM"`; `""` if missing or invalid
 */
export const formatLocalizedDateTime = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const day = String(date.date()).padStart(2, "0");
  const months = LANGUAGE === "ar" ? ARABIC_MONTHS : ENGLISH_MONTHS;
  const month = months[date.month()];
  const year = date.year();

  return `${day} ${month} ${year} | ${date.format("hh:mm A")}`;
};

// Arabic counts its nouns: يوم واحد، يومان، 3–10 أيام، 11 يومًا فأكثر.
const arabicDays = (count) => {
  if (count === 1) return "يوم واحد";
  if (count === 2) return "يومان";
  if (count <= 10) return `${count} أيام`;
  return `${count} يومًا`;
};

// Hours and minutes of a span under a day, for formatDurationFromNow.
const hoursAndMinutes = (minutes) => {
  const units =
    LANGUAGE === "ar"
      ? { hour: "س", minute: "د" }
      : { hour: "h", minute: "min" };
  const total = Math.max(Math.round(Number(minutes) || 0), 0);

  if (total < 60) return `${total} ${units.minute}`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest
    ? `${hours} ${units.hour} ${rest} ${units.minute}`
    : `${hours} ${units.hour}`;
};

/**
 * The span between now and a moment as a compact duration — "3 d", "2 h 15 min", "35 min" —
 * with no direction: callers word it ("due in …", "late by …") through their own i18n keys.
 * It counts forward as well as back, unlike `sinceWhen`, which only reads the past.
 * @param {string|number|Date|import("dayjs").Dayjs} dateString - Value parseable by dayjs
 * @returns {string} Compact duration; `""` if missing or invalid
 */
export const formatDurationFromNow = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const minutes = Math.abs(date.diff(dayjs(), "minute"));

  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return LANGUAGE === "ar" ? arabicDays(days) : `${days} d`;
  }
  return hoursAndMinutes(Math.max(minutes, 1));
};

/**
 * Formats an ISO (or parseable) datetime with `Date.prototype.toLocaleString` for locale-aware output.
 * Uses `ar-SA` when `LANGUAGE` is Arabic, otherwise `en-US`; `dateStyle: "medium"`, `timeStyle: "short"`.
 * @param {string|number|Date} isoString - ISO 8601 string or timestamp; empty values yield `""`
 * @returns {string} Localized medium date + short time; on `toLocaleString` failure falls back to `"YYYY-MM-DD HH:mm"`;
 *   if dayjs cannot parse the input, returns `String(isoString)` so callers still see something
 */

export const getFormattedDateTime = (date, active) => {
  if (!date) return "";

  // ✅ Detect and fix "DD-MM-YYYY HH:mm:ss" format manually
  let formattedDateStr = date;
  if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
    const [day, month, yearAndTime] = date.split("-");
    const [year, time] = yearAndTime.split(" ");
    formattedDateStr = `${year}-${month}-${day} ${time || ""}`; // -> "YYYY-MM-DD HH:mm:ss"
  }

  const now = new Date(formattedDateStr);
  const isArabic = LANGUAGE === "ar";

  const arabicOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    numberingSystem: "arab",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const englishOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const formattedDate = now.toLocaleDateString(
    isArabic ? "ar-EG" : LANGUAGE,
    isArabic
      ? arabicOptions
      : { ...englishOptions, hour: undefined, minute: undefined }
  );

  const formattedTime = now.toLocaleTimeString(
    isArabic ? "ar-EG" : LANGUAGE,
    isArabic ? arabicOptions : englishOptions
  );

  return (
    <div
      className={`flex justify-center gap-1.5 border rounded-[4px] w-fit px-1.5 py-1 ${
        active ? "border-primary" : "border-[#1F2A37]"
      }`}
    >
      <Calendar2 active={active} />
      <span
        className={`text-[13px] font-normal ${
          active ? "text-primary" : "text-[#1F2A37]"
        }`}
      >
        {!isArabic ? formattedTime : formattedDate}
      </span>
    </div>
  );
};

export const getFormattedDateTime2 = (date, hasTime = true) => {
  const now = new Date(date);

  let formattedDate;
  let formattedTime;

  if (LANGUAGE === "ar") {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    formattedDate = now.toLocaleDateString("ar-EG-u-nu-arab", options);
    formattedTime = now.toLocaleTimeString("ar-EG-u-nu-arab", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate}  ${hasTime ? "-" + formattedTime : ""}`;
  } else {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    formattedDate = now.toLocaleDateString(LANGUAGE, options);
    formattedTime = now.toLocaleTimeString(LANGUAGE, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate}  ${hasTime ? "-" + formattedTime : ""}`;
  }
};

export const getTime = (dateString) => {
  const date = new Date(dateString);

  let hours = date.getHours();
  const minutes = date.getMinutes();

  if (LANGUAGE === "en") {
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  } else if (LANGUAGE === "ar") {
    const period = hours >= 12 ? "مساء" : "صباحا";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  return dateString;
};
